import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  ELSCheckBox,
  ELSDropDownOption,
  ELSTextBox,
  ELSDateTime
} from '@els/els-component-form-field-react';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { flowRight, isNil } from 'lodash';
import { ELSCommonConfig } from '@els/els-ui-common-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import {
  copyRaasAssessment,
  fetchRaasAssessments,
  putRaaSAssessment,
  regenerateAssessmentRecommendations
} from '../../apis/nhe-remediation-service/nhe-remediation-service.utilities';
import {
  RaasAssessmentDto,
  RaasAssessmentSourceTypeDto,
  RaasAssessmentWithSourcesDto,
  RecommendationGenerationStatusDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { BulkCancelModal } from './BulkCancelModal.component';
import {
  ELSDataTable,
  ELSDropDown
} from '../../components/els.components';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  fetchUserAction,
  trackNewPageLoadAction
} from '../../components/app/app-state-actions.utilities';
import { ServerConstants } from '../../components/app/server.constants';
import {
  isInstructor,
  isNumberish
} from '../../utilities/common.utilities';
import { RecordEmpty } from '../../models/common.models';
import { tableDataRowHandler } from '../../utilities/remediation.utilities';
import { UserDto } from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import {
  loadAdminFilters,
  renderPlpSourceTypes,
  saveAdminFilters
} from './admin.utilities';
import { AdminComponentState } from './admin.models';
import { PLP_SOURCE_TYPE_LABELS, VALID_PLP_SOURCE_TYPE_FILTERS } from './admin.constants';

const defaultState: AdminComponentState = {
  raasAssessmentsDto: null,
  selectedStatuses: {},
  selectedPage: 0,
  selectedAssessmentIdMap: {},
  pageSize: 50,
  batchResponseDto: null,
  userIdInput: '',
  studentStudyEolsAssessmentIdInput: '',
  remediationAssignmentIdInput: '',
  courseSectionIdInput: '',
  raasAssessmentIdInput: '',
  environmentInput: ELSCommonConfig.appProfile,
  createdAtFromInput: new Date(),
  createdAtFromEnabled: false,
  createdAtToInput: new Date(),
  createdAtToEnabled: false,
  hasRegeneratedToIdFilter: null,
  hasRegeneratedFromIdFilter: null,
  isActiveFilter: null,
  sourceTypeFilter: null
};

const pageSizes = [50, 100, 200, 500, 1000];

const getDropdownOptions = (state: AdminComponentState): ELSDropDownOption[] => {
  if (!state.raasAssessmentsDto) {
    return [
      { value: 0, name: 'Loading...' }
    ];
  }
  return Array.from({ length: state.raasAssessmentsDto.totalPages }, (_, i): ELSDropDownOption => ({
    value: i,
    name: `Page ${i + 1}`
  }));
};

type AdminComponentPropsOnly = RecordEmpty;
type AdminComponentProps = AdminComponentPropsOnly
  & ELSPropsFromToastService
  & ELSPropsFromModalService;

// eslint-disable-next-line sonarjs/cognitive-complexity
const AdminComponent = (props: AdminComponentProps) => {

  const { appState, appStateRef, setAppState } = useContext(AppContext);

  // Load persisted filters from session storage
  const persistedFilters = loadAdminFilters(defaultState);

  const [state, setState] = useState<AdminComponentState>({
    ...persistedFilters
  });

  const isShowCopyBtn = (row: RaasAssessmentWithSourcesDto): boolean => {

    if (!row.raasAssessment.courseSectionId) {
      return false;
    }

    if (appState.courseSectionId !== row.raasAssessment.courseSectionId.toString()) {
      return false;
    }

    // Instructor admins can copy anyone in their course section
    if (isInstructor(appState.userRole)) {
      return true;
    }

    // Student admins can copy only themselves
    return appState.userId === row.raasAssessment.userId.toString();
  };

  const updateAssessments = () => {

    const status = Object.keys(state.selectedStatuses)
      .filter((key) => {
        return state.selectedStatuses[key];
      })
      .map((key) => {
        return key as RecommendationGenerationStatusDto;
      });

    fetchRaasAssessments({
      status,
      userId: isNumberish(state.userIdInput) ? [Number(state.userIdInput)] : null,
      remediationAssignmentId: isNumberish(state.remediationAssignmentIdInput) ? [Number(state.remediationAssignmentIdInput)] : null,
      id: isNumberish(state.raasAssessmentIdInput) ? [Number(state.raasAssessmentIdInput)] : null,
      studentStudyEolsAssessmentId: isNumberish(state.studentStudyEolsAssessmentIdInput) ? [Number(state.studentStudyEolsAssessmentIdInput)] : null,
      courseSectionId: isNumberish(state.courseSectionIdInput) ? [Number(state.courseSectionIdInput)] : null,
      environment: state.environmentInput ? [state.environmentInput] : null,
      createdAtFrom: state.createdAtFromEnabled ? state.createdAtFromInput.toISOString() : null,
      createdAtTo: state.createdAtToEnabled ? state.createdAtToInput.toISOString() : null,
      hasRegeneratedToId: state.hasRegeneratedToIdFilter,
      hasRegeneratedFromId: state.hasRegeneratedFromIdFilter,
      isActive: state.isActiveFilter,
      sourceType: state.sourceTypeFilter ? [state.sourceTypeFilter] : null,
      page: state.selectedPage,
      pageSize: state.pageSize
    }).then((raasAssessmentsDto) => {
      setState((prevState) => {
        return {
          ...prevState,
          raasAssessmentsDto
        };
      });
    }).finally(() => {
      trackNewPageLoadAction({ appState, appStateRef, setAppState });
    });
  };

  // Persist filter state to session storage whenever filters change
  useEffect(() => {
    saveAdminFilters(state);
  }, [
    state.userIdInput,
    state.studentStudyEolsAssessmentIdInput,
    state.remediationAssignmentIdInput,
    state.courseSectionIdInput,
    state.raasAssessmentIdInput,
    state.environmentInput,
    state.createdAtFromInput,
    state.createdAtFromEnabled,
    state.createdAtToInput,
    state.createdAtToEnabled,
    state.hasRegeneratedToIdFilter,
    state.hasRegeneratedFromIdFilter,
    state.isActiveFilter,
    state.sourceTypeFilter,
    state.selectedPage,
    state.selectedStatuses,
    state.pageSize
  ]);

  useEffect(() => {
    updateAssessments();
  }, [
    state.userIdInput,
    state.studentStudyEolsAssessmentIdInput,
    state.remediationAssignmentIdInput,
    state.courseSectionIdInput,
    state.raasAssessmentIdInput,
    state.environmentInput,
    state.createdAtFromInput,
    state.createdAtFromEnabled,
    state.createdAtToInput,
    state.createdAtToEnabled,
    state.hasRegeneratedToIdFilter,
    state.hasRegeneratedFromIdFilter,
    state.isActiveFilter,
    state.sourceTypeFilter,
    state.selectedPage,
    state.selectedStatuses,
    state.pageSize
  ]);

  const selectCheckbox = (row: RaasAssessmentWithSourcesDto) => {
    // Show checkbox when we can retry OR cancel
    // Can retry: inactive assessments that haven't been regenerated
    // Can cancel: assessments that are not already cancelled
    const canRetry = row.raasAssessment.isActive === false && !row.raasAssessment.regeneratedToId;
    const canCancel = row.raasAssessment.recGenerationStatus !== RecommendationGenerationStatusDto.CANCELLED_ADMIN_REQUEST;

    if (canRetry || canCancel) {
      return (
        <ELSCheckBox
          changeHandler={() => {
            setState((prevState) => {
              return {
                ...prevState,
                selectedAssessmentIdMap: {
                  ...prevState.selectedAssessmentIdMap,
                  [row.raasAssessment.id]: !prevState.selectedAssessmentIdMap[row.raasAssessment.id]
                }
              };
            });
          }}
          checked={!!state.selectedAssessmentIdMap[row.raasAssessment.id]}
        />
      );
    }
    return null;
  };

  const retryButton = (row: RaasAssessmentWithSourcesDto) => {
    // Only show retry button for inactive assessments that haven't been regenerated
    if (row.raasAssessment.isActive === false && !row.raasAssessment.regeneratedToId) {
      return (
        <button
          type="button"
          className="u-els-debuttonize u-els-anchorize"
          onClick={() => {
            regenerateAssessmentRecommendations([row.raasAssessment.id]).then((batchResponseDto) => {
              updateAssessments();
              if (batchResponseDto && batchResponseDto.rejectedIds.length > 0) {
                props.toastService.openToast({
                  ...BASE_TOAST_CONFIG,
                  component: <div>Assessment {row.raasAssessment.id} was rejected</div>,
                  type: ELSWithToastService.types.NEGATIVE,
                });
              } else {
                props.toastService.openToast({
                  ...BASE_TOAST_CONFIG,
                  component: <div>Retry initiated for assessment {row.raasAssessment.id}</div>,
                  type: ELSWithToastService.types.POSITIVE,
                });
              }
            });
          }}
        >
          retry
        </button>
      );
    }
    return null;
  };

  const confirmationOptions = (row: RaasAssessmentWithSourcesDto) => {

    if (row.raasAssessment.isActive === true) {
      return (
        <div>
          <button
            type="button"
            className="u-els-debuttonize u-els-anchorize"
            onClick={() => {
              const modalId = 'CONFIRM_SET_INACTIVE';
              props.modalService.openConfirmModal({
                modalId,
                confirmHandler: () => {
                  putRaaSAssessment({
                    ...row.raasAssessment,
                    isActive: false
                  }).then(() => {
                    updateAssessments();
                    props.modalService.closeModal(modalId);
                  });
                },
                cancelHandler: () => {
                  props.modalService.closeModal(modalId);
                },
                content: (
                  <div>
                    <h2 className="u-els-color-warn">!!DANGER!!</h2>
                    <div className="o-els-container">Are you sure you want to set this to inactive which will remove it from student access?</div>
                    <div>
                      <pre>{JSON.stringify(row.raasAssessment, null, 2)}</pre>
                    </div>
                  </div>
                ),
              });
            }}
          >
            set inactive
          </button>
        </div>
      );
    }

    return (
      <div>
        <button
          type="button"
          className="u-els-debuttonize u-els-anchorize"
          onClick={() => {
            const modalId = 'CONFIRM_SET_ACTIVE';
            props.modalService.openConfirmModal({
              modalId,
              confirmHandler: () => {
                putRaaSAssessment({
                  ...row.raasAssessment,
                  isActive: true
                }).then(() => {
                  updateAssessments();
                  props.modalService.closeModal(modalId);
                });
              },
              cancelHandler: () => {
                props.modalService.closeModal(modalId);
              },
              content: (
                <div>
                  <h2 className="u-els-color-warn">!!CONFIRM!!</h2>
                  <div className="o-els-container">Are you sure you want to set this to active which will make it accessible to students?</div>
                  <div className="o-els-container u-els-color-warn u-els-margin-top-1x">
                    <strong>WARNING:</strong> If there are multiple active assessments with the same studentStudyEolsAssessmentId ({row.raasAssessment.studentStudyEolsAssessmentId}), it will cause errors. There should only be one active RaaS assessment for each studentStudyEolsAssessmentId.
                  </div>
                  <div>
                    <pre>{JSON.stringify(row.raasAssessment, null, 2)}</pre>
                  </div>
                </div>
              ),
            });
          }}
        >
          set active
        </button>
      </div>
    );
  };

  const cancelAdminRequest = (row: RaasAssessmentWithSourcesDto) => {
    // Hide button if already cancelled
    if (row.raasAssessment.recGenerationStatus === RecommendationGenerationStatusDto.CANCELLED_ADMIN_REQUEST) {
      return null;
    }

    return (
      <div>
        <button
          type="button"
          className="u-els-debuttonize u-els-anchorize"
          onClick={() => {
            const modalId = 'CONFIRM_CANCEL_ADMIN_REQUEST';
            props.modalService.openConfirmModal({
              modalId,
              confirmHandler: () => {
                putRaaSAssessment({
                  ...row.raasAssessment,
                  recGenerationStatus: RecommendationGenerationStatusDto.CANCELLED_ADMIN_REQUEST,
                  isActive: false
                }).then(() => {
                  updateAssessments();
                  props.modalService.closeModal(modalId);
                });
              },
              cancelHandler: () => {
                props.modalService.closeModal(modalId);
              },
              content: (
                <div>
                  <h2 className="u-els-color-warn">!!CONFIRM!!</h2>
                  <div className="o-els-container">Are you sure you want to cancel this assessment via admin request? This will set the status to CANCELLED_ADMIN_REQUEST and set isActive to false.</div>
                  <div>
                    <pre>{JSON.stringify(row.raasAssessment, null, 2)}</pre>
                  </div>
                </div>
              ),
            });
          }}
        >
          cancel
        </button>
      </div>
    );
  };

  const confirmationButton = (row: RaasAssessmentWithSourcesDto) => {
    return (
      <div>

        {isShowCopyBtn(row) && (
          <button
            type="button"
            className="u-els-debuttonize u-els-anchorize"
            onClick={() => {
              const modalId = 'CONFIRM_COPY';
              props.modalService.openConfirmModal({
                modalId,
                confirmHandler: () => {
                  copyRaasAssessment({
                    raasAssessmentId: row.raasAssessment.id,
                    newUserId: row.raasAssessment.userId
                  }).then((newRaasAssessment) => {
                    props.toastService.openToast({
                      ...BASE_TOAST_CONFIG,
                      component: <div>Copied assessment with ID: {newRaasAssessment.id}</div>,
                      type: ELSWithToastService.types.POSITIVE,
                    });
                    updateAssessments();
                    props.modalService.closeModal(modalId);
                  }).catch((error) => {
                    props.toastService.openToast({
                      ...BASE_TOAST_CONFIG,
                      component: <div>Error copying assessment: {error.message}</div>,
                      type: ELSWithToastService.types.NEGATIVE,
                    });
                  });
                },
                cancelHandler: () => {
                  props.modalService.closeModal(modalId);
                },
                content: (
                  <div>
                    <h2 className="u-els-color-warn">!!CONFIRM!!</h2>
                    <div className="o-els-container">
                      Are you sure you want to copy this assessment?
                      Both the original assessment and the copied assessment will be active.
                      Active assessments are visible and accessible to students and instructors.
                    </div>
                    <div>
                      <pre>{JSON.stringify(row.raasAssessment, null, 2)}</pre>
                    </div>
                  </div>
                ),
              });
            }}
          >
            copy
          </button>
        )}
      </div>
    );

  };

  const launchButton = (row: RaasAssessmentWithSourcesDto) => {
    return (
      <div>
        <button
          type="button"
          className="u-els-debuttonize u-els-anchorize"
          onClick={() => {
            fetchUserAction({
              appState,
              appStateRef,
              setAppState,
              userId: row.raasAssessment.userId.toString()
            })
              .then((user: UserDto) => {
                if (!user) {
                  props.toastService.openToast({
                    ...BASE_TOAST_CONFIG,
                    component: <div>Failed to fetch user information</div>,
                    type: ELSWithToastService.types.NEGATIVE,
                  });
                  return;
                }

                const { appLinkToolUrl } = ServerConstants[ServerConstants.DataSource];
                const linkOutBody = JSON.stringify({ assessmentId: row.raasAssessment.studentStudyEolsAssessmentId });

                const params = new URLSearchParams({
                  USER_SEARCH_STRING: user.emailAddress,
                  USER_ID: row.raasAssessment.userId.toString(),
                  COURSE_SECTION_ID: row.raasAssessment.courseSectionId.toString(),
                  ACTION: 'ASSESSMENT_START',
                  LINK_OUT_BODY: linkOutBody,
                  TARGET_APP: 'REMEDIATION',
                  ADVANCED_OPTIONS: 'true'
                });

                const url = `${appLinkToolUrl}/#/launch-pad?${params.toString()}`;
                window.open(url, '_blank');
              })
              .catch((error) => {
                props.toastService.openToast({
                  ...BASE_TOAST_CONFIG,
                  component: <div>Error fetching user: {error.message}</div>,
                  type: ELSWithToastService.types.NEGATIVE,
                });
              });
          }}
        >
          launch
        </button>
      </div>
    );
  };
  const clearAllFilters = () => {
    setState((prevState) => {
      return {
        ...prevState,
        userIdInput: defaultState.userIdInput,
        studentStudyEolsAssessmentIdInput: defaultState.studentStudyEolsAssessmentIdInput,
        remediationAssignmentIdInput: defaultState.remediationAssignmentIdInput,
        courseSectionIdInput: defaultState.courseSectionIdInput,
        raasAssessmentIdInput: defaultState.raasAssessmentIdInput,
        environmentInput: '',
        createdAtFromInput: defaultState.createdAtFromInput,
        createdAtFromEnabled: defaultState.createdAtFromEnabled,
        createdAtToInput: defaultState.createdAtToInput,
        createdAtToEnabled: defaultState.createdAtToEnabled,
        hasRegeneratedToIdFilter: defaultState.hasRegeneratedToIdFilter,
        hasRegeneratedFromIdFilter: defaultState.hasRegeneratedFromIdFilter,
        isActiveFilter: defaultState.isActiveFilter,
        sourceTypeFilter: defaultState.sourceTypeFilter,
        selectedStatuses: defaultState.selectedStatuses,
        selectedPage: defaultState.selectedPage
      };
    });
  };

  const renderClickableId = (row: RaasAssessmentDto, key: string, filterField: string) => {
    if (isNil(row[key])) {
      return '';
    }
    return (
      <button
        type="button"
        className="u-els-debuttonize u-els-anchorize"
        onClick={() => {
          setState((prevState) => {
            return {
              ...prevState,
              [filterField]: row[key].toString(),
              selectedPage: 0
            };
          });
        }}
      >
        {row[key].toString()}
      </button>
    );
  };

  const renderTotalTime = (row: RaasAssessmentWithSourcesDto) => {
    if (!row.raasAssessment.startTime || !row.raasAssessment.endTime) {
      return '';
    }
    const startDate = new Date(row.raasAssessment.startTime);
    const endDate = new Date(row.raasAssessment.endTime);
    const diffMs = endDate.getTime() - startDate.getTime();

    if (diffMs < 0) {
      return 'Invalid';
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

    return <span className="u-els-nowrap">{parts.join(' ')}</span>;
  };

  const calculateMetrics = () => {
    if (!state.raasAssessmentsDto || !state.raasAssessmentsDto.content) {
      return {
        completedCount: 0,
        errorCount: 0,
        notStartedCount: 0,
        averageTime: 'N/A'
      };
    }

    const assessments = state.raasAssessmentsDto.content;

    const completedCount = assessments.filter(a => a.raasAssessment.recGenerationStatus === RecommendationGenerationStatusDto.COMPLETED).length;
    const errorCount = assessments.filter(a => a.raasAssessment.recGenerationStatus === RecommendationGenerationStatusDto.ERROR).length;
    const notStartedCount = assessments.filter(a => a.raasAssessment.recGenerationStatus === RecommendationGenerationStatusDto.NOT_STARTED).length;

    // Calculate average time for completed assessments
    const completedAssessments = assessments.filter(a => (
      a.raasAssessment.recGenerationStatus === RecommendationGenerationStatusDto.COMPLETED &&
      a.raasAssessment.startTime &&
      a.raasAssessment.endTime));

    let averageTime = 'N/A';
    if (completedAssessments.length > 0) {
      const totalMs = completedAssessments.reduce((sum, assessment) => {
        const startDate = new Date(assessment.raasAssessment.startTime);
        const endDate = new Date(assessment.raasAssessment.endTime);
        const diffMs = endDate.getTime() - startDate.getTime();
        return sum + (diffMs > 0 ? diffMs : 0);
      }, 0);

      const avgMs = totalMs / completedAssessments.length;
      const hours = Math.floor(avgMs / (1000 * 60 * 60));
      const minutes = Math.floor((avgMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((avgMs % (1000 * 60)) / 1000);

      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

      averageTime = parts.join(' ');
    }

    return {
      completedCount,
      errorCount,
      notStartedCount,
      averageTime
    };
  };

  const metrics = calculateMetrics();

  return (
    <div className="u-els-padding-2x">
      <h1>Admin</h1>

      <div className="c-els-card o-els-container">
        <div className="c-els-card__content">

          <div className="">

            <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
              <FlexItem>
                <ELSTextBox
                  value={state.environmentInput}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        environmentInput: value,
                        selectedPage: 0 // Reset to first page on search
                      };
                    });
                  }}
                >
                  Environment
                </ELSTextBox>
                <div>
                  <small>Current environment: {defaultState.environmentInput}</small>
                </div>
              </FlexItem>

              <FlexItem>
                <ELSDropDown
                  value={state.pageSize}
                  options={pageSizes.map((size) => ({
                    value: size,
                    name: `Page Size ${size}`
                  }))}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        pageSize: value as number,
                        selectedPage: 0 // Reset to first page on page size change
                      };
                    });
                  }}
                >
                  Page Size
                </ELSDropDown>
              </FlexItem>

              <FlexItem>
                <ELSDropDown
                  value={state.selectedPage}
                  options={getDropdownOptions(state)}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        selectedPage: value as number
                      };
                    });
                  }}
                >
                  Page
                </ELSDropDown>
              </FlexItem>
            </FlexLayout>
          </div>
          <div className="o-els-container">

            <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
              <FlexItem>
                <ELSTextBox
                  value={state.userIdInput}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        userIdInput: value,
                        selectedPage: 0 // Reset to first page on page size change
                      };
                    });
                  }}
                >
                  User ID
                </ELSTextBox>
                <div>
                  <small>Current userId: {appState.userId}</small>
                </div>
              </FlexItem>

              <FlexItem>
                <ELSTextBox
                  value={state.courseSectionIdInput}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        courseSectionIdInput: value,
                        selectedPage: 0 // Reset to first page on search
                      };
                    });
                  }}
                >
                  Course Section ID
                </ELSTextBox>
                <div>
                  <small>Current courseSectionId: {appState.courseSectionId}</small>
                </div>
              </FlexItem>

            </FlexLayout>
          </div>
          <div className="o-els-container">

            <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
              <FlexItem>
                <ELSTextBox
                  value={state.raasAssessmentIdInput}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        raasAssessmentIdInput: value,
                        selectedPage: 0 // Reset to first page on search
                      };
                    });
                  }}
                >
                  RaaS Assessment ID
                </ELSTextBox>
              </FlexItem>
              <FlexItem>
                <ELSTextBox
                  value={state.studentStudyEolsAssessmentIdInput}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        studentStudyEolsAssessmentIdInput: value,
                        selectedPage: 0 // Reset to first page on search
                      };
                    });
                  }}
                >
                  Student Study EOLS Assessment ID
                </ELSTextBox>
              </FlexItem>

              <FlexItem>
                <ELSTextBox
                  value={state.remediationAssignmentIdInput}
                  changeHandler={(e, value) => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        remediationAssignmentIdInput: value,
                        selectedPage: 0 // Reset to first page on search
                      };
                    });
                  }}
                >
                  Remediation Assignment ID
                </ELSTextBox>
              </FlexItem>


            </FlexLayout>
          </div>

          <div className="o-els-container">

            <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
              <FlexItem>
                <div>
                  <ELSCheckBox
                    changeHandler={() => {
                      setState((prevState) => {
                        return {
                          ...prevState,
                          createdAtFromEnabled: !prevState.createdAtFromEnabled,
                          selectedPage: 0
                        };
                      });
                    }}
                    checked={state.createdAtFromEnabled}
                  >
                    Created At From
                  </ELSCheckBox>
                  {
                    state.createdAtFromEnabled && (
                      <ELSDateTime
                        id="createdAtFrom"
                        name="createdAtFrom"
                        value={state.createdAtFromInput}
                        disableAfter={state.createdAtToInput}
                        changeHandler={(e, value) => {
                          setState((prevState) => {
                            return {
                              ...prevState,
                              createdAtFromInput: value,
                              selectedPage: 0 // Reset to first page on search
                            };
                          });
                        }}
                      />
                    )
                  }
                </div>
              </FlexItem>

              <FlexItem>
                <div>
                  <ELSCheckBox
                    changeHandler={() => {
                      setState((prevState) => {
                        return {
                          ...prevState,
                          createdAtToEnabled: !prevState.createdAtToEnabled,
                          selectedPage: 0
                        };
                      });
                    }}
                    checked={state.createdAtToEnabled}
                  >
                    Created At To
                  </ELSCheckBox>
                  {
                    state.createdAtToEnabled && (
                      <ELSDateTime
                        id="createdAtTo"
                        name="createdAtTo"
                        value={state.createdAtToInput}
                        disableBefore={state.createdAtFromInput}
                        changeHandler={(e, value) => {
                          setState((prevState) => {
                            return {
                              ...prevState,
                              createdAtToInput: value,
                              selectedPage: 0 // Reset to first page on search
                            };
                          });
                        }}
                      />
                    )
                  }
                </div>
              </FlexItem>
            </FlexLayout>
          </div>


          <div>
            <div>Statuses</div>
            <FlexLayout modifiers={[FlexLayoutModifier.LEFT]}>
              <FlexItem>
                {
                  Object.values(RecommendationGenerationStatusDto)
                    .map((statusValue) => {
                      return (
                        <div
                          key={statusValue}
                          className="o-els-container o-els-container--1o4">
                          <ELSCheckBox
                            checked={state.selectedStatuses[statusValue]}
                            changeHandler={() => {
                              setState((prevState) => {
                                const newStatuses = {
                                  ...prevState.selectedStatuses,
                                  [statusValue]: !prevState.selectedStatuses[statusValue]
                                };
                                return {
                                  ...prevState,
                                  selectedStatuses: newStatuses
                                };
                              });
                            }}
                          >
                            {statusValue}
                          </ELSCheckBox>
                        </div>
                      );
                    })
                }
              </FlexItem>
            </FlexLayout>
          </div>

          <div className="o-els-container">
            <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
              <FlexItem>
                <ELSDropDown
                  value={state.hasRegeneratedToIdFilter === null ? 'all' : state.hasRegeneratedToIdFilter.toString()}
                  options={[
                    { value: 'all', name: 'All' },
                    { value: 'true', name: 'Yes' },
                    { value: 'false', name: 'No' }
                  ]}
                  changeHandler={(e, value) => {
                    setState((prevState) => ({
                      ...prevState,
                      hasRegeneratedToIdFilter: value === 'all' ? null : value === 'true',
                      selectedPage: 0
                    }));
                  }}
                >
                  Has Regenerated To
                </ELSDropDown>
              </FlexItem>
              <FlexItem>
                <ELSDropDown
                  value={state.hasRegeneratedFromIdFilter === null ? 'all' : state.hasRegeneratedFromIdFilter.toString()}
                  options={[
                    { value: 'all', name: 'All' },
                    { value: 'true', name: 'Yes' },
                    { value: 'false', name: 'No' }
                  ]}
                  changeHandler={(e, value) => {
                    setState((prevState) => ({
                      ...prevState,
                      hasRegeneratedFromIdFilter: value === 'all' ? null : value === 'true',
                      selectedPage: 0
                    }));
                  }}
                >
                  Has Regenerated From
                </ELSDropDown>
              </FlexItem>
              <FlexItem>
                <ELSDropDown
                  value={state.isActiveFilter === null ? 'all' : state.isActiveFilter.toString()}
                  options={[
                    { value: 'all', name: 'All' },
                    { value: 'true', name: 'Active' },
                    { value: 'false', name: 'Inactive' }
                  ]}
                  changeHandler={(e, value) => {
                    setState((prevState) => ({
                      ...prevState,
                      isActiveFilter: value === 'all' ? null : value === 'true',
                      selectedPage: 0
                    }));
                  }}
                >
                  Is Active
                </ELSDropDown>
              </FlexItem>
              <FlexItem>
                <ELSDropDown
                  value={state.sourceTypeFilter === null ? 'all' : state.sourceTypeFilter}
                  options={[
                    { value: 'all', name: 'All' },
                    ...VALID_PLP_SOURCE_TYPE_FILTERS.map((value) => ({ value, name: PLP_SOURCE_TYPE_LABELS[value] }))
                  ]}
                  changeHandler={(e, value) => {
                    setState((prevState) => ({
                      ...prevState,
                      sourceTypeFilter: value === 'all' ? null : value as RaasAssessmentSourceTypeDto,
                      selectedPage: 0
                    }));
                  }}
                >
                  PLP Source Type
                </ELSDropDown>
              </FlexItem>
            </FlexLayout>
          </div>

        </div>
      </div>


      <div className="o-els-container">
        <ELSButton
          type={ELSButtonType.SECONDARY}
          size={ELSButtonSize.SMALL}
          onClick={clearAllFilters}
        >
          Clear All Filters
        </ELSButton>
      </div>

      {
        state.raasAssessmentsDto && (
          <div>
            <h3>RaaS Assessments</h3>

            <div className="o-els-container">
              {state.raasAssessmentsDto.numberOfElements} of {state.raasAssessmentsDto.totalElements} items
            </div>

            <div className="o-els-container">
              <div className="c-els-card">
                <div className="c-els-card__content">
                  <h4 className="u-els-margin-bottom-1o2">Page Metrics</h4>
                  <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
                    <FlexItem>
                      <div>
                        <strong>Completed:</strong> {metrics.completedCount}
                      </div>
                    </FlexItem>
                    <FlexItem>
                      <div>
                        <strong>Error:</strong> {metrics.errorCount}
                      </div>
                    </FlexItem>
                    <FlexItem>
                      <div>
                        <strong>Not Started:</strong> {metrics.notStartedCount}
                      </div>
                    </FlexItem>
                    <FlexItem>
                      <div>
                        <strong>Avg Time (Completed):</strong> {metrics.averageTime}
                      </div>
                    </FlexItem>
                  </FlexLayout>
                </div>
              </div>
            </div>

            <div className="o-els-container">
              <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_1o2]}>
                <FlexItem>
                  <ELSButton
                    type={ELSButtonType.SECONDARY}
                    size={ELSButtonSize.SMALL}
                    onClick={() => {
                      // Select all assessments that can be retried or cancelled
                      const newSelectionMap = {};
                      state.raasAssessmentsDto.content.forEach((row) => {
                        const canRetry = row.raasAssessment.isActive === false && !row.raasAssessment.regeneratedToId;
                        const canCancel = row.raasAssessment.recGenerationStatus !== RecommendationGenerationStatusDto.CANCELLED_ADMIN_REQUEST;
                        if (canRetry || canCancel) {
                          newSelectionMap[row.raasAssessment.id] = true;
                        }
                      });
                      setState((prevState) => ({
                        ...prevState,
                        selectedAssessmentIdMap: newSelectionMap
                      }));
                    }}
                  >
                    Select All
                  </ELSButton>
                </FlexItem>
                <FlexItem>
                  <ELSButton
                    type={ELSButtonType.SECONDARY}
                    size={ELSButtonSize.SMALL}
                    onClick={() => {
                      setState((prevState) => ({
                        ...prevState,
                        selectedAssessmentIdMap: {}
                      }));
                    }}
                  >
                    Deselect All
                  </ELSButton>
                </FlexItem>
                <FlexItem>
                  <ELSButton
                    type={ELSButtonType.SECONDARY}
                    size={ELSButtonSize.SMALL}
                    onClick={() => {
                      // Get IDs that are visible on current page
                      const visibleIds = state.raasAssessmentsDto.content.map(a => a.raasAssessment.id);

                      const selectedIds = Object.keys(state.selectedAssessmentIdMap)
                        .filter((key) => {
                          return state.selectedAssessmentIdMap[key];
                        })
                        .map((key) => {
                          return parseInt(key, 10);
                        })
                        .filter((id) => {
                          return visibleIds.includes(id);
                        });

                      if (selectedIds.length === 0) {
                        props.toastService.openToast({
                          ...BASE_TOAST_CONFIG,
                          component: <div>No assessments selected on current page</div>,
                          type: ELSWithToastService.types.NEGATIVE,
                        });
                        return;
                      }

                      regenerateAssessmentRecommendations(selectedIds).then((batchResponseDto) => {
                        updateAssessments();
                        if (batchResponseDto && batchResponseDto.rejectedIds.length > 0) {
                          props.toastService.openToast({
                            ...BASE_TOAST_CONFIG,
                            component: <div>Some assessments were rejected: {batchResponseDto.rejectedIds.join(', ')}</div>,
                            type: ELSWithToastService.types.NEGATIVE,
                          });
                        }
                      });
                    }}
                  >
                    Retry selected assessments
                  </ELSButton>
                </FlexItem>
                <FlexItem>
                  <ELSButton
                    type={ELSButtonType.SECONDARY}
                    size={ELSButtonSize.SMALL}
                    onClick={() => {
                      // Get IDs that are visible on current page
                      const visibleIds = state.raasAssessmentsDto.content.map(a => a.raasAssessment.id);

                      const selectedIds = Object.keys(state.selectedAssessmentIdMap)
                        .filter((key) => {
                          return state.selectedAssessmentIdMap[key];
                        })
                        .map((key) => {
                          return parseInt(key, 10);
                        })
                        .filter((id) => {
                          return visibleIds.includes(id);
                        });

                      if (selectedIds.length === 0) {
                        props.toastService.openToast({
                          ...BASE_TOAST_CONFIG,
                          component: <div>No assessments selected on current page</div>,
                          type: ELSWithToastService.types.NEGATIVE,
                        });
                        return;
                      }

                      const modalId = 'CONFIRM_BULK_CANCEL';
                      props.modalService.openConfirmModal({
                        modalId,
                        confirmHandler: () => {
                          props.modalService.closeModal(modalId);

                          // Get selected assessments that are visible on current page
                          const selectedAssessments = state.raasAssessmentsDto.content
                            .filter((assessment) => {
                              return selectedIds.includes(assessment.raasAssessment.id);
                            })
                            .map((assessment) => {
                              return assessment.raasAssessment;
                            });

                          const progressModalId = 'BULK_CANCEL_PROGRESS';

                          // Open progress modal with BulkCancelModal component
                          props.modalService.openModal({
                            modalId: progressModalId,
                            content: (
                              <BulkCancelModal
                                selectedAssessments={selectedAssessments}
                                putRaaSAssessment={putRaaSAssessment}
                                onComplete={(successCount, failureCount, failedIds) => {
                                  // Refresh the table after all cancellations
                                  updateAssessments();

                                  // Show summary toast
                                  if (failureCount === 0) {
                                    props.toastService.openToast({
                                      ...BASE_TOAST_CONFIG,
                                      component: <div>Successfully cancelled {successCount} assessment(s)</div>,
                                      type: ELSWithToastService.types.POSITIVE,
                                    });
                                  } else {
                                    props.toastService.openToast({
                                      ...BASE_TOAST_CONFIG,
                                      component: (
                                        <div>
                                          Cancelled {successCount} assessment(s).
                                          Failed to cancel {failureCount} assessment(s): {failedIds.join(', ')}
                                        </div>
                                      ),
                                      type: ELSWithToastService.types.NEGATIVE,
                                    });
                                  }
                                }}
                                onClose={() => {
                                  props.modalService.closeModal(progressModalId);
                                }}
                              />
                            ),
                          });
                        },
                        cancelHandler: () => {
                          props.modalService.closeModal(modalId);
                        },
                        content: (
                          <div>
                            <h2 className="u-els-color-warn">!!CONFIRM!!</h2>
                            <div className="o-els-container">
                              Are you sure you want to cancel {selectedIds.length} assessment(s)?
                              This will set their status to CANCELLED_ADMIN_REQUEST and set isActive to false.
                            </div>
                            <div className="o-els-container u-els-margin-top-1x">
                              <strong>Selected IDs:</strong> {selectedIds.join(', ')}
                            </div>
                          </div>
                        ),
                      });
                    }}
                  >
                    Cancel selected assessments
                  </ELSButton>
                </FlexItem>
              </FlexLayout>
            </div>

            <div className="o-els-container u-els-nowrap">
              <ELSDataTable
                data={state.raasAssessmentsDto.content}
                noWrap
                sortIconSize="1o2"
                cssModifier="c-els-table--vertical-dividers"
                columns={[
                  {
                    header: "Select",
                    customRender: (row: RaasAssessmentWithSourcesDto) => selectCheckbox(row)
                  },

                  {
                    header: "Retry",
                    customRender: (row: RaasAssessmentWithSourcesDto) => retryButton(row)
                  },

                  {
                    header: "Launch",
                    customRender: (row: RaasAssessmentWithSourcesDto) => launchButton(row)
                  },

                  {
                    header: "Active/Inactive",
                    customRender: (row: RaasAssessmentWithSourcesDto) => confirmationOptions(row)
                  },

                  {
                    header: "Cancel (Admin)",
                    customRender: (row: RaasAssessmentWithSourcesDto) => cancelAdminRequest(row)
                  },

                  {
                    header: "Copy",
                    customRender: (row: RaasAssessmentWithSourcesDto) => confirmationButton(row)
                  },

                  {
                    key: "regeneratedToId",
                    header: "regeneratedToId",
                    field: "regeneratedToId",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'regeneratedToId', 'raasAssessmentIdInput')
                  },
                  {
                    key: "regeneratedFromId",
                    header: "regeneratedFromId",
                    field: "regeneratedFromId",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'regeneratedFromId', 'raasAssessmentIdInput')
                  },
                  {
                    key: "id",
                    header: "id",
                    field: "id",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'id', 'raasAssessmentIdInput')
                  },
                  {
                    key: "recGenerationStatus",
                    header: "recGenerationStatus",
                    field: "recGenerationStatus",
                    customRender: (row: RaasAssessmentWithSourcesDto) => {
                      return isNil(row.raasAssessment.recGenerationStatus) ? '' : row.raasAssessment.recGenerationStatus.toString();
                    }
                  },
                  {
                    key: "isActive",
                    header: "isActive",
                    field: "isActive",
                    customRender: (row: RaasAssessmentWithSourcesDto) => {
                      return isNil(row.raasAssessment.isActive) ? '' : row.raasAssessment.isActive.toString();
                    }
                  },
                  {
                    key: "plpSourceType",
                    header: "PLP Source Type",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderPlpSourceTypes(row.sourceList)
                  },
                  {
                    key: "studentStudyEolsAssessmentId",
                    header: "studentStudyEolsAssessmentId",
                    field: "studentStudyEolsAssessmentId",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'studentStudyEolsAssessmentId', 'studentStudyEolsAssessmentIdInput')
                  },
                  {
                    key: "remediationAssignmentId",
                    header: "remediationAssignmentId",
                    field: "remediationAssignmentId",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'remediationAssignmentId', 'remediationAssignmentIdInput')
                  },
                  {
                    key: "courseSectionId",
                    header: "courseSectionId",
                    field: "courseSectionId",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'courseSectionId', 'courseSectionIdInput')
                  },
                  {
                    key: "userId",
                    header: "userId",
                    field: "userId",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'userId', 'userIdInput')
                  },
                  {
                    key: "totalTime",
                    header: "Total Time",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderTotalTime(row)
                  },
                  {
                    key: "environment",
                    header: "environment",
                    field: "environment",
                    customRender: (row: RaasAssessmentWithSourcesDto) => renderClickableId(row.raasAssessment, 'environment', 'environmentInput')
                  },
                  ...[
                    'startTime',
                    'endTime',
                    'lastAccessed',
                    'createdAt',
                    'updatedAt',
                    'queueUntilAfter'
                  ].map((key) => (
                    {
                      key,
                      header: key,
                      field: key,
                      customRender: (row: RaasAssessmentWithSourcesDto) => tableDataRowHandler(
                        [(!row.raasAssessment[key]), <></>],
                        <span className="u-els-nowrap">{new Date(row.raasAssessment[key]).toLocaleString()}</span>
                      )
                    }
                  ))
                ]}
              >
              </ELSDataTable>
            </div>
          </div>
        )
      }
    </div>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Admin' }),
  ELSWithModalService,
  ELSWithToastService,
  withPageLoader,
);

const Admin = enhance(AdminComponent) as React.FC<AdminComponentPropsOnly>;
export default Admin;
